export type OrderLike = {
  state?: string;
  status?: string;
  payments?: { status?: string | null }[];
  shipments?: { status?: string | null }[];
  creditMemos?: { status?: string | null }[];
};

const norm = (v: any) => String(v ?? "").trim().toLowerCase();

// Los `status` de `sales` que el listado y el panel de la home agrupan, para filtrar con `in`. Tiene
// que reproducir `ORDER_GROUP_SQL` de xcon-api, que cuenta abierto TODO lo que no es completo ni
// cancelado: un estado que falta acá sale en el número del panel y no en el listado al que lleva
// (xcons-docs#480 — `in_shipping`, `payment_review` y `facturado` faltaban).
export const ORDER_STATUS_GROUPS = {
  open: [
    "invoiced",
    "facturado",
    "in_transit",
    "in_shipping",
    "review",
    "payment_review",
    "pending",
    "pending_payment",
    "processing",
    "preparing",
  ],
  complete: ["complete", "closed"],
  // `devuelto` es cancelado, no completo (PM, xcons-docs#300).
  canceled: ["canceled", "devuelto"],
} as const;

export const deriveOrderShipmentStatus = (order: OrderLike): "open" | "complete" | "canceled" => {
  const state = norm(order.state);
  const status = norm(order.status);

  if (status === "complete" && state === "in_shipping") {
    return "open";
  }

  const payments = order.payments ?? [];
  const shipments = order.shipments ?? [];

  const hasPaymentCanceled = payments.some(
    (p) => norm(p.status) === "canceled",
  );

  const hasPaymentVerified = payments.some(
    (p) => norm(p.status) === "payment_verified",
  );

  const allShipmentsDelivered =
    shipments.length > 0 &&
    shipments.every((s) => norm(s.status) === "delivered");

  const allShipmentsCanceled =
    shipments.length > 0 &&
    shipments.every((s) => norm(s.status) === "canceled");

  // Una nota de crédito que no llegó a Pago verificado es trabajo pendiente: el reembolso todavía
  // no se hizo (xcons-docs#559).
  const hasPendingCreditMemo = (order.creditMemos ?? []).some(
    (cm) => norm(cm.status) !== "payment_verified",
  );

  // `devuelto` es cancelado, no completo (PM, xcons-docs#300): un pedido que volvió no se vendió.
  // Va ANTES de "pagado y todo entregado → completo": un devuelto normalmente ya estaba pagado y
  // entregado, y si se evaluara después seguiría saliendo como completo.
  if (["canceled", "devuelto"].includes(state) || ["canceled", "devuelto"].includes(status)) {
    return "canceled";
  }

  // Pago cancelado, o todas las entregas canceladas (p. ej. cancelación bulk de todas las
  // entregas pendientes): el pedido es cancelado recién cuando sus notas de crédito están en Pago
  // verificado. Hasta entonces sigue En curso (Diseño, xcons-docs#559). Es el mismo momento en que
  // la API pasa el pedido a `canceled` (`maybeFinalizeOrderInMagento`), así que el badge y el
  // filtro del listado coinciden. Sin nota de crédito (cancelado antes de facturar) es inmediato.
  if (hasPaymentCanceled || allShipmentsCanceled) {
    return hasPendingCreditMemo ? "open" : "canceled";
  }

  if (hasPaymentVerified && allShipmentsDelivered) return "complete";

  if (["complete", "closed"].includes(state) || ["complete", "closed"].includes(status)) {
    return "complete";
  }

  return "open";
};
